"use client";

import * as React from "react";
import { User } from "lucide-react";
import { cn, resolveUserLabel } from "../../utils";
import { PropertyTrigger } from "./property-trigger";
import { SelectedCheck } from "./selected-check";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { IdentityAvatar } from "../../data-display/identity-avatar";
import { isMatch, MatchedText } from "../../data-display/matched-text";
import { IdentityDisplay } from "../identity-display";
import type { TaskIdentitySummary } from "./task-detail-view";

// Substring on the shown label, like the mention popup, so every listed row
// has a highlighted match; cmdk's default fuzzy score would list rows that don't.
function filterByLabel(_value: string, search: string, keywords?: string[]): number {
  return keywords?.some((keyword) => isMatch(keyword, search)) ? 1 : 0;
}

function identityLabel(identity: TaskIdentitySummary): string {
  return (
    resolveUserLabel({ name: identity.name, email: identity.email, id: identity.publicId }) ??
    identity.publicId
  );
}

export interface AssigneeSelectorProps {
  /** All assignable identities */
  options: TaskIdentitySummary[];
  /** Currently selected identity */
  value?: TaskIdentitySummary | null | undefined;
  /** Callback when assignee changes */
  onSelect: (identity: TaskIdentitySummary | null) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export function AssigneeSelector({
  options,
  value = null,
  onSelect,
  disabled = false,
}: AssigneeSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [highlighted, setHighlighted] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Pin the width measured at open (the unfiltered list, so the longest row) as
  // a floor, so the popover doesn't shrink under the cursor while searching.
  const lockOpenWidth = React.useCallback((el: HTMLDivElement | null) => {
    if (el && !el.style.minWidth) el.style.minWidth = `${el.offsetWidth}px`;
  }, []);

  const users = options.filter((o) => o.kind === "user");
  const agents = options.filter((o) => o.kind === "agent");

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const handleSelect = (identity: TaskIdentitySummary | null) => {
    onSelect(identity);
    handleOpenChange(false);
  };

  // Mirrors filterByLabel: a lone group of results needs no heading, since the
  // avatar shape already says whether a row is a person or an agent.
  const hasResults = (group: TaskIdentitySummary[]) =>
    group.some((identity) => !search || isMatch(identityLabel(identity), search));
  const showGroupHeadings = hasResults(users) && hasResults(agents);

  const renderIdentityRow = (identity: TaskIdentitySummary) => {
    const label = identityLabel(identity);
    return (
      <CommandItem
        key={identity.publicId}
        value={identity.publicId}
        keywords={[label]}
        onSelect={() => handleSelect(identity)}
        className="py-2"
      >
        <IdentityAvatar
          kind={identity.kind}
          id={identity.publicId}
          name={label}
          email={identity.email}
          avatarUrl={identity.avatarUrl}
          size="sm"
        />
        <MatchedText
          text={label}
          query={search}
          selected={highlighted === identity.publicId}
          title={label}
          className="min-w-0"
        />
        {value?.publicId === identity.publicId && <SelectedCheck />}
      </CommandItem>
    );
  };

  React.useEffect(() => {
    if (open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <PropertyTrigger
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("shrink", !value && "text-muted-foreground")}
        >
          <span className="inline-flex min-w-0 items-center gap-2">
            {value ? (
              <IdentityDisplay
                kind={value.kind}
                name={value.name}
                publicId={value.publicId}
                email={value.email}
                avatarUrl={value.avatarUrl}
                variant="inline"
              />
            ) : (
              <>
                <User className="h-3.5 w-3.5 shrink-0" />
                <span>Assignee</span>
              </>
            )}
          </span>
        </PropertyTrigger>
      </PopoverTrigger>
      <PopoverContent
        ref={lockOpenWidth}
        className="w-max min-w-[220px] max-w-[min(26rem,calc(100vw-2rem))] p-0"
        align="start"
        collisionPadding={16}
      >
        <Command filter={filterByLabel} value={highlighted} onValueChange={setHighlighted}>
          <CommandInput
            ref={inputRef}
            value={search}
            onValueChange={setSearch}
            placeholder="Search…"
            className="focus:outline-none focus:ring-0"
          />
          <CommandList>
            <CommandEmpty>No matches found.</CommandEmpty>

            {/* Unassign option */}
            <CommandGroup>
              <CommandItem
                value="unassigned"
                keywords={["Unassigned"]}
                onSelect={() => handleSelect(null)}
                className="py-2"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
                <span className="text-sm text-muted-foreground">Unassigned</span>
                {!value && <SelectedCheck />}
              </CommandItem>
            </CommandGroup>

            {users.length > 0 && (
              <CommandGroup heading={showGroupHeadings ? "Users" : undefined}>
                {users.map(renderIdentityRow)}
              </CommandGroup>
            )}

            {agents.length > 0 && (
              <CommandGroup heading={showGroupHeadings ? "Agents" : undefined}>
                {agents.map(renderIdentityRow)}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
